import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Bell, IndianRupee, Calendar, ShieldCheck, Phone, X, CheckCircle2, MessageCircle, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function OwnerNotifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null); // full booking detail modal
  const [confirming, setConfirming] = useState(false);
  const wrapRef = useRef();

  const load = () => api.get("/owner/notifications").then((r) => {
    setItems(r.data.items || []);
    setUnread(r.data.unread || 0);
  }).catch(() => {});

  useEffect(() => { load(); const t = setInterval(load, 45000); return () => clearInterval(t); }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target) && !expanded) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, expanded]);

  const onOpen = async () => {
    setOpen(true);
    if (unread > 0) {
      try {
        await api.post("/owner/notifications/mark-seen");
        setUnread(0);
        setItems((prev) => prev.map((i) => ({ ...i, seen_by_owner: true })));
      } catch { /* silent */ }
    }
  };

  const confirmToGuest = async (b) => {
    setConfirming(true);
    try {
      await api.post(`/owner/bookings/${b.id}/confirm`);
      toast.success(`Confirmed to ${b.guest_name || 'guest'} — they've been notified.`);
      setItems((prev) => prev.map((i) => i.id === b.id ? { ...i, owner_confirmed: true, owner_confirmed_at: new Date().toISOString() } : i));
      setExpanded((prev) => prev ? { ...prev, owner_confirmed: true } : prev);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not confirm booking');
    } finally { setConfirming(false); }
  };

  const digits = (p) => {
    const d = String(p || "").replace(/\D/g, "");
    return d.length === 10 ? "91" + d : d;
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        data-testid="owner-notif-bell"
        onClick={onOpen}
        className="relative w-11 h-11 rounded-full bg-white border border-marigold-200 hover:border-vermilion-600 hover:bg-marigold-50 grid place-items-center transition-colors"
        aria-label="Notifications"
      >
        <Bell size={18} className="text-vermilion-600" />
        {unread > 0 && (
          <span data-testid="owner-notif-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-bandhani-600 text-white text-[10px] font-black grid place-items-center border-2 border-white animate-pulse">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="owner-notif-panel" className="absolute right-0 mt-2 w-[92vw] max-w-md bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-br from-marigold-50 to-turmeric-100">
            <div>
              <div className="text-[10px] tracking-widest uppercase font-bold text-vermilion-600">Paid bookings</div>
              <div className="font-display font-black text-lg">Recent activity</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded-full hover:bg-white"><X size={16} /></button>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {items.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">No paid bookings yet. When students pay, you'll see them here.</div>
            )}
            {items.map((it) => (
              <button
                key={it.id}
                type="button"
                data-testid={`owner-notif-${it.id}`}
                onClick={() => setExpanded(it)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-marigold-50 transition-colors ${!it.seen_by_owner ? "bg-turmeric-50" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full grid place-items-center flex-shrink-0 ${it.owner_confirmed ? 'bg-peacock-500 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                    {it.owner_confirmed ? <CheckCircle2 size={16} /> : <IndianRupee size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-sm truncate">{it.guest_name || "Student"}</div>
                      <div className="font-display font-black text-sm text-emerald-700">₹{Math.round(it.amount).toLocaleString("en-IN")}</div>
                      {it.owner_confirmed && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-peacock-500 text-white font-bold uppercase tracking-widest">Confirmed</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-600 truncate">{it.hostel_name}</div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Calendar size={11} /> {it.check_in} → {it.check_out} · {it.beds} bed(s)</div>
                    {it.verification_code && (
                      <div className="text-[10px] font-mono mt-1 inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-1.5 py-0.5">
                        <ShieldCheck size={9} /> {it.verification_code}
                      </div>
                    )}
                    <div className="text-[10px] text-vermilion-600 font-semibold mt-1">Tap to view · confirm to guest →</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Full detail modal */}
      {expanded && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setExpanded(null)}>
          <div
            data-testid="owner-notif-detail"
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 bg-peacock-gradient text-white flex items-start justify-between">
              <div>
                <div className="text-[10px] tracking-widest uppercase font-bold text-turmeric-300">New paid booking</div>
                <div className="font-display font-black text-2xl mt-1">₹{Math.round(expanded.amount).toLocaleString("en-IN")}</div>
                <div className="text-xs text-white/85 mt-1">{expanded.hostel_name}</div>
              </div>
              <button onClick={() => setExpanded(null)} className="p-1 rounded-full hover:bg-white/15"><X size={18} /></button>
            </div>

            <div className="p-6 space-y-4">
              {/* Guest info */}
              <div className="bg-marigold-50 border border-marigold-200 rounded-xl p-4">
                <div className="text-[10px] tracking-widest uppercase font-bold text-vermilion-600">Guest</div>
                <div className="mt-1 font-semibold text-lg">{expanded.guest_name || "Student"}</div>
                {expanded.guest_phone && (
                  <a href={`tel:${expanded.guest_phone}`} className="mt-1 inline-flex items-center gap-1.5 text-sm font-mono text-vermilion-700 hover:underline">
                    <Phone size={13} /> {expanded.guest_phone}
                  </a>
                )}
                {expanded.guest_email && (
                  <a href={`mailto:${expanded.guest_email}`} className="mt-1 block text-sm text-slate-700 hover:underline">
                    <Mail size={12} className="inline mr-1" /> {expanded.guest_email}
                  </a>
                )}
              </div>

              {/* Stay info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <div className="text-[9px] tracking-widest uppercase font-bold text-slate-500">Check-in</div>
                  <div className="font-semibold text-sm mt-0.5">{expanded.check_in}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <div className="text-[9px] tracking-widest uppercase font-bold text-slate-500">Check-out</div>
                  <div className="font-semibold text-sm mt-0.5">{expanded.check_out}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <div className="text-[9px] tracking-widest uppercase font-bold text-slate-500">Beds</div>
                  <div className="font-semibold text-sm mt-0.5">{expanded.beds} × {expanded.nights} night(s)</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <div className="text-[9px] tracking-widest uppercase font-bold text-slate-500">Meals / Room</div>
                  <div className="font-semibold text-sm mt-0.5">{expanded.include_meal ? "Meals included" : "No meals"}{expanded.room_type ? ` · ${expanded.room_type}` : ""}</div>
                </div>
              </div>

              {/* Verification code */}
              {expanded.verification_code && (
                <div className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-4">
                  <div className="text-[10px] tracking-widest uppercase font-bold text-emerald-700">Verification code · ask guest to show this on arrival</div>
                  <div data-testid="detail-verification-code" className="mt-1 font-mono font-black text-2xl tracking-widest text-emerald-800 select-all">{expanded.verification_code}</div>
                </div>
              )}

              {/* Actions */}
              <div className="pt-2 flex flex-wrap gap-2">
                {expanded.guest_phone && (
                  <a
                    href={`https://wa.me/${digits(expanded.guest_phone)}?text=${encodeURIComponent(`Hi ${expanded.guest_name || ''}, this is ${expanded.hostel_name}. Your booking is confirmed. Please carry your ID and mention verification code ${expanded.verification_code || ''} on arrival.`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold"
                  ><MessageCircle size={14} /> WhatsApp guest</a>
                )}
                {expanded.owner_confirmed ? (
                  <div className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-peacock-500 text-white text-sm font-bold" data-testid="already-confirmed">
                    <CheckCircle2 size={14} /> Already confirmed to guest
                  </div>
                ) : (
                  <Button
                    data-testid="confirm-to-guest-btn"
                    disabled={confirming}
                    onClick={() => confirmToGuest(expanded)}
                    className="rounded-full bg-vermilion-600 hover:bg-vermilion-700 text-white h-10 px-5 gap-1.5"
                  >
                    {confirming ? <><Loader2 className="animate-spin" size={14} /> Confirming…</> : <><CheckCircle2 size={14} /> Confirm to guest</>}
                  </Button>
                )}
              </div>
              <div className="text-[11px] text-slate-500">
                Confirming sends a "you're expected" email + in-app note to the guest with your name + phone. They can't be scammed by fake owners after you confirm.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
